/**
 * CartDrawer.jsx — Definitive version
 *
 * Free shipping on orders above Rs. 500. updateQuantity(itemId, size, newQty) is absolute quantity.
 * Add to index.js: import 'wicg-inert'; npm install wicg-inert for Safari < 15.4.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../../context/CartContext';
import { useAuth } from '../../../context/AuthContext';
import {
  IconArrowRight,
  IconClose,
  IconMinus,
  IconPlus,
  IconTrash,
  IconShoppingBag,
  IconLock,
  IconReturn,
} from '../../common/Icons/Icons';
import { formatCurrency } from '../../../utils/currency';
import { getDeliveryDate } from '../../../utils/delivery';
import ApiImage from '../../common/ApiImage';
import { FREE_SHIPPING_THRESHOLD, STANDARD_SHIPPING_RATE, getFreeShippingRemaining } from '../../../config/shipping';
import './CartDrawer.css';

const FALLBACK_IMG = '/placeholder-jewelry.svg';

function useFocusTrap(isOpen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const el = ref.current;
    const sel = 'a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])';
    const get = () => [...el.querySelectorAll(sel)];
    const t = setTimeout(() => get()[0]?.focus({ preventScroll: true }), 280);
    const fn = (e) => {
      if (e.key !== 'Tab') return;
      const nodes = get();
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    el.addEventListener('keydown', fn);
    return () => { clearTimeout(t); el.removeEventListener('keydown', fn); };
  }, [isOpen]);
  return ref;
}

/* ── GPU cleanup after slide animation ───────────────────── */
function useWillChangeCleanup(ref, isOpen) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.willChange = 'transform';
    const done = () => { el.style.willChange = 'auto'; };
    el.addEventListener('transitionend', done, { once: true });
    return () => el.removeEventListener('transitionend', done);
  }, [isOpen, ref]);
}

/* ── CartItem ────────────────────────────────────────────── */
function CartItem({ item, onRemove, onQtyChange }) {
  const [removing, setRemoving] = useState(false);
  const [qty, setQty] = useState(item.quantity);

  useEffect(() => { setQty(item.quantity); }, [item.quantity]);

  const remove = async () => {
    setRemoving(true);
    try { await onRemove(item); }
    catch { setRemoving(false); }
  };

  const changeQty = (delta) => {
    const next = qty + delta;
    if (next < 1) { remove(); return; }
    setQty(next);
    onQtyChange(item, next);
  };

  const lineTotal  = (item.price || 0) * qty;
  const lineSaving = item.originalPrice > item.price
    ? (item.originalPrice - item.price) * qty : 0;

  return (
    <article
      className={`cdi${removing ? ' cdi--out' : ''}`}
      aria-label={`${item.name}, ${formatCurrency(lineTotal)}, qty ${qty}`}
    >
      <Link
        to={`/product/${item.slug || item._id || item.id}`}
        className="cdi__thumb"
        tabIndex={-1}
        aria-hidden="true"
      >
        <ApiImage
          src={item.image || item.images?.[0]}
          fallback={FALLBACK_IMG}
          alt=""
          className="cdi__img"
          width="72"
          height="90"
          loading="lazy"
          decoding="async"
        />
        {lineSaving > 0 && (
          <span className="cdi__save-badge" aria-hidden="true">SALE</span>
        )}
      </Link>

      <div className="cdi__body">
        <Link to={`/product/${item.slug || item._id || item.id}`} className="cdi__name">
          {item.name}
        </Link>

        {item.size && (
          <p className="cdi__meta">
            Size {item.size}
          </p>
        )}

        <div className="cdi__bottom">
          {/* Stepper — no overflow:hidden, no clipping on iOS */}
          <div className="cdi__stepper" role="group" aria-label={`Quantity for ${item.name}`}>
            <button
              type="button"
              className="cdi__step cdi__step--dec"
              onClick={() => changeQty(-1)}
              disabled={removing}
              aria-label={qty === 1 ? `Remove ${item.name}` : 'Decrease quantity'}
            >
              {qty === 1 ? <IconTrash size={13} /> : <IconMinus size={11} />}
            </button>

            <span className="cdi__count" aria-live="polite" aria-atomic="true">
              {qty}
            </span>

            <button
              type="button"
              className="cdi__step cdi__step--inc"
              onClick={() => changeQty(1)}
              disabled={removing}
              aria-label="Increase quantity"
            >
              <IconPlus size={11} />
            </button>
          </div>

          <div className="cdi__prices">
            <span className="cdi__price">{formatCurrency(lineTotal)}</span>
            {lineSaving > 0 && (
              <span className="cdi__saved">−{formatCurrency(lineSaving)}</span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

/* ── Empty state ─────────────────────────────────────────── */
function EmptyState({ onClose }) {
  return (
    <div className="cd-empty">
      <div className="cd-empty__icon"><IconShoppingBag size={56} /></div>
      <p className="cd-empty__h">Your jewelry collection awaits</p>
      <p className="cd-empty__p">Add pieces you love to your bag.</p>
      <div className="cd-empty__btns">
        <Link to="/shop/women" className="cd-empty__btn" onClick={onClose}>
          Explore Collections
        </Link>
        <Link to="/women" className="cd-empty__btn cd-empty__btn--o" onClick={onClose}>
          Women&apos;s Jewelry
        </Link>
      </div>
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────── */
export default function CartDrawer({ isOpen, onClose }) {
  const { cartItems, cartSummary, removeFromCart, updateQuantity } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate  = useNavigate();
  const drawerRef = useFocusTrap(isOpen);
  useWillChangeCleanup(drawerRef, isOpen);

  const count    = cartItems.reduce((s, i) => s + i.quantity, 0);
  const subtotal = cartSummary?.subtotal ?? 0;
  const discount = cartSummary?.discountAmount ?? 0;
  const qualifyingSubtotal = Math.max(0, subtotal);
  const shippingFee = qualifyingSubtotal >= FREE_SHIPPING_THRESHOLD ? 0 : STANDARD_SHIPPING_RATE;
  const remainingForFreeShipping = getFreeShippingRemaining(qualifyingSubtotal);
  const total    = cartSummary?.total ?? Math.max(0, subtotal + shippingFee - discount);
  const deliveryWindow = getDeliveryDate(5, 8);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', isOpen);
    return () => document.body.classList.remove('no-scroll');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [isOpen, onClose]);

  const handleRemove = useCallback(
    (item) => removeFromCart(item._id || item.id, item.size, item.color),
    [removeFromCart]
  );

  const handleQtyChange = useCallback(
    (item, newQty) => updateQuantity(item._id || item.id, item.size, newQty, item.color),
    [updateQuantity]
  );

  const goCheckout = useCallback(() => {
    onClose();
    // Guests go to login/signup page with redirect back to checkout after auth
    if (!isAuthenticated) {
      navigate('/login?redirect=checkout');
      return;
    }
    navigate('/checkout');
  }, [navigate, onClose, isAuthenticated]);

  return (
    <>
      <div
        className={`cd-overlay${isOpen ? ' cd-overlay--on' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Your bag, ${count} ${count === 1 ? 'piece' : 'pieces'}`}
        className={`cd${isOpen ? ' cd--open' : ''}`}
        inert={isOpen ? undefined : true}
      >
        {/* Header */}
        <header className="cd-head">
          <h2 className="cd-head__title">
            Your Bag
            {count > 0 && <span className="cd-head__badge" aria-hidden="true">{count}</span>}
          </h2>
          <button type="button" className="cd-head__close" onClick={onClose} aria-label="Close bag">
            <IconClose size={18} />
          </button>
        </header>

        {/* Items */}
        <div className="cd-body" role="list" aria-label="Bag items">
          {cartItems.length === 0
            ? <EmptyState onClose={onClose} />
            : cartItems.map(item => (
                <div
                  key={`${item._id || item.id}||${item.size || ''}`}
                  role="listitem"
                >
                  <CartItem
                    item={item}
                    onRemove={handleRemove}
                    onQtyChange={handleQtyChange}
                  />
                </div>
              ))
          }
        </div>

        {/* Shipping progress */}
        {cartItems.length > 0 && (
          <div className="cd-ship" aria-live="polite" aria-label="Shipping progress">
            <p className="cd-ship__msg cd-ship__msg--on">
              {shippingFee === 0
                ? 'You have unlocked free shipping'
                : `Add ${formatCurrency(remainingForFreeShipping)} more to unlock free shipping`}
            </p>
            <p className="cd-ship__eta">
              Estimated delivery: {deliveryWindow}
            </p>
          </div>
        )}

        {/* Footer */}
        {cartItems.length > 0 && (
          <footer className="cd-foot">

            {/* Trust row — calm, no emoji */}
            <ul className="cd-trust">
              <li><IconLock size={12} /> Secure</li>
              <li aria-hidden="true" className="cd-trust__dot">·</li>
              <li><IconReturn size={12} /> Returns for size &amp; defective only</li>
              <li aria-hidden="true" className="cd-trust__dot">·</li>
              <li>Prepaid only</li>
            </ul>

            {/* Price summary */}
            <dl className="cd-sum">
              <div className="cd-sum__row">
                <dt>Subtotal</dt>
                <dd>{formatCurrency(subtotal)}</dd>
              </div>
              <div className="cd-sum__row">
                <dt>Shipping</dt>
                <dd className="cd-sum__free">{shippingFee === 0 ? 'Free' : formatCurrency(shippingFee)}</dd>
              </div>
              {cartSummary.discountAmount > 0 && (
                <div className="cd-sum__row cd-sum__row--discount">
                  <dt>Discount{cartSummary.couponCode ? ` (${cartSummary.couponCode})` : ''}</dt>
                  <dd>−{formatCurrency(cartSummary.discountAmount)}</dd>
                </div>
              )}
              <div className="cd-sum__row cd-sum__row--total">
                <dt>Total</dt>
                <dd>{formatCurrency(total)}</dd>
              </div>
            </dl>

            {discount > 0 && (
              <p className="cd-foot__saved" aria-live="polite">
                You saved {formatCurrency(discount)} on this order
              </p>
            )}

            <p className="cd-foot__note">Incl. taxes · Free shipping on orders above ₹500</p>

            {/* Trust at decision point (Baymard/NNG: security adjacent to CTA = higher completion) */}
            <p className="cd-foot__secure" aria-label="Secure checkout">
              <IconLock size={12} aria-hidden="true" /> Secure checkout
            </p>

            {/* Primary CTA — "Checkout Now" when single item for higher conversion */}
            <button type="button" className="cd-checkout" onClick={goCheckout}>
              <span>{cartItems.length === 1 ? 'Checkout Now' : 'Checkout'}</span>
              <span className="cd-checkout__sep" aria-hidden="true">·</span>
              <span>{formatCurrency(total)}</span>
              <span className="cd-checkout__arrow" aria-hidden="true"><IconArrowRight size={14} /></span>
            </button>

            {!isAuthenticated && (
              <Link to="/login?redirect=checkout" className="cd-foot__nudge" onClick={onClose}>
                Sign in for faster checkout
              </Link>
            )}

            <Link to="/cart" className="cd-foot__full" onClick={onClose}>
              View full bag &amp; apply coupon →
            </Link>
          </footer>
        )}
      </div>
    </>
  );
}